/**
 * Matters List API Service - Infinite scroll wrapper
 *
 * This service wraps the generated matters list API with infinite query support.
 * Pattern follows policeDepartmentsService.ts approach.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// Type defined locally since generated file has no matching endpoints yet
export type MatterListRead = {
  id: string;
  name: string;
  status: string;
  firm: string;
  created_at: string;
  [key: string]: unknown;
};

export interface MattersListFilter {
  search?: string;
  status?: string[];
  firm?: string[];
  matterType?: string[];
  city?: string;
  state?: string;
  representingAttorney?: string;
  dateOfLossAfter?: string;
  dateOfLossBefore?: string;
  createdAtAfter?: string;
  createdAtBefore?: string;
  ordering?: string;
  /** Filter by case manager(s) - use 'unassigned' for matters without a case manager */
  caseManager?: string[];
}

export interface MattersListSearchResults {
  count: number;
  results: MatterListRead[];
}

export const mattersListApiClient = createApi({
  reducerPath: 'mattersListApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['MattersList'],
  endpoints: (builder) => ({
    getMattersInfinite: builder.infiniteQuery<
      MattersListSearchResults,
      MattersListFilter,
      {
        offset: number;
        limit: number;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          offset: 0,
          limit: 100,
        },
        getNextPageParam: (lastPage, pages, lastPageParam) => {
          const totalFetched = lastPageParam.offset + lastPageParam.limit;
          if (totalFetched >= lastPage.count) return undefined;
          return {
            offset: totalFetched,
            limit: lastPageParam.limit,
          };
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
          if (firstPageParam.offset === 0) return undefined;
          return {
            offset: Math.max(0, firstPageParam.offset - firstPageParam.limit),
            limit: firstPageParam.limit,
          };
        },
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams({
          limit: pageParam.limit.toString(),
          offset: pageParam.offset.toString(),
        });

        if (queryArg.search) params.set('search', queryArg.search);
        if (queryArg.status && queryArg.status.length > 0) {
          queryArg.status.forEach((s) => params.append('status', s));
        }
        if (queryArg.firm && queryArg.firm.length > 0) {
          queryArg.firm.forEach((f) => params.append('firm', f));
        }
        if (queryArg.matterType && queryArg.matterType.length > 0) {
          queryArg.matterType.forEach((t) => params.append('matter_type', t));
        }
        if (queryArg.city) params.set('city', queryArg.city);
        if (queryArg.state) params.set('state', queryArg.state);
        if (queryArg.representingAttorney)
          params.set('representing_attorney', queryArg.representingAttorney);
        if (queryArg.dateOfLossAfter)
          params.set('date_of_loss_after', queryArg.dateOfLossAfter);
        if (queryArg.dateOfLossBefore)
          params.set('date_of_loss_before', queryArg.dateOfLossBefore);
        if (queryArg.createdAtAfter)
          params.set('created_at_after', queryArg.createdAtAfter);
        if (queryArg.createdAtBefore)
          params.set('created_at_before', queryArg.createdAtBefore);
        if (queryArg.ordering) params.set('ordering', queryArg.ordering);
        if (queryArg.caseManager && queryArg.caseManager.length > 0) {
          // Backend accepts comma-separated UUIDs
          params.set('case_manager', queryArg.caseManager.join(','));
        }

        return {
          url: `api/matters-list/?${params.toString()}`,
        };
      },
      providesTags: (result, error, filter) => [
        {
          type: 'MattersList',
          id: JSON.stringify({ ...filter, offset: undefined }),
        },
        { type: 'MattersList' },
      ],
    }),
  }),
});

export const { useGetMattersInfiniteInfiniteQuery } = mattersListApiClient;
